import type { PropsWithChildren } from 'react';
import React, { useEffect, useRef } from 'react';
import { useInvitePeopleContext } from '../../components/InvitePeople';
import { getExperimentValue } from '../../feature-gate';
import { useMemo } from 'react';
import { CollectionsContext, CollectionsContextState } from './CollectionsContext';
import { BaseInvitePeopleAnalytics, useAnalytics } from '../../analytics';
import { AnalyticsAction, AnalyticsActionSubject } from '../../types';
import { isEnglishLocale } from '../../utils';
import { useIntl } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';

class TwcForCoreInvitesExperimentContextAnalytics extends BaseInvitePeopleAnalytics {
	twcForCoreInvitesExposed(attributes: { twcForCoreInvitesCohort: string }) {
		const { productTitle, source, subProduct } = this.commonAttributes;
		this.fireTrack({
			action: AnalyticsAction.EXPOSED,
			actionSubject: AnalyticsActionSubject.INVITE_PEOPLE_FEATURE,
			actionSubjectId: 'twcForCoreInvites',
			attributes: {
				product: productTitle,
				source,
				subProduct,
				...attributes,
			},
		});
	}
}

const collectionsContextValue = { collections: {}, shouldFetchCollections: true };

/*
    Puma experiment: Puma-54 TWC for Core Invites
    go/puma-experiments
    Due to low TWC traffic the experiment is evaluating if the additional TWC check impacts new PEU, whether the UI change is shown is not evaluated.
*/
export const TwcForCoreInvitesExperimentContext = ({ children }: PropsWithChildren) => {
	const { locale } = useIntl();
	const { cloudId } = useInvitePeopleContext();

	const twcForCoreInvitesCohort = useMemo(() => {
		if (!isEnglishLocale(locale)) {
			return 'not-enrolled';
		}
		return getExperimentValue('twc-for-core-invites', 'not-enrolled', true);
	}, [locale]);
	const analytics = useAnalytics(TwcForCoreInvitesExperimentContextAnalytics);
	const hasFired = useRef<boolean>();

	useEffect(() => {
		if (!hasFired.current && ['variant', 'control'].includes(twcForCoreInvitesCohort)) {
			hasFired.current = true;
			analytics.twcForCoreInvitesExposed({ twcForCoreInvitesCohort });
		}
	}, [analytics, twcForCoreInvitesCohort]);

	if (twcForCoreInvitesCohort === 'variant') {
		if (fg('core-invites-fetch-collections')) {
			return (
				<CollectionsContextState.Provider value={collectionsContextValue}>
					{children}
				</CollectionsContextState.Provider>
			);
		}

		return <CollectionsContext cloudId={cloudId}>{children}</CollectionsContext>;
	}

	return children;
};
